<script setup lang="ts">
  import type { SystemModule } from '@/config/app'

  import { useSessionTracking } from '@/shared/lib'
  import { useAppConfig } from '@/shared/lib/tenant'

  import { useProfile } from '@/entities/profile'

  import { CoinsBurnNotification } from '@/features/coinsBurnNotification'

  import { BalanceInfo, BirthdaysCard, EventsAnnouncements, HelpCard, LastNews, UserInfo } from '@/widgets/home'

  const { t } = useI18n()

  definePage({
    meta: { layout: 'baseNew' },
  })

  useHead({
    title: t('navigation_item_home'),
  })

  const { startSession, startHeartbeat } = useSessionTracking()

  onMounted(() => {
    startSession()
    const cleanup = startHeartbeat()

    onUnmounted(() => {
      cleanup()
    })
  })

  type Card = {
    component: Component
    module: SystemModule
    props?: Record<string, string>
  }

  const CARDS_LIST: { left: Card[]; right: Card[] } = {
    left: [
      { module: 'profile', component: UserInfo },
      { module: 'news', component: LastNews },
      { module: 'faq', component: HelpCard, props: { type: 'faq' } },
      { module: 'feedback', component: HelpCard, props: { type: 'feedback' } },
    ],
    right: [
      { module: 'shop', component: BalanceInfo },
      { module: 'events', component: EventsAnnouncements },
      { module: 'profiles', component: BirthdaysCard },
    ],
  }

  const { enabledSystemModules } = useAppConfig()

  const isModuleEnabled = (module: SystemModule) => enabledSystemModules.includes(module)
  const userStore = useProfile()
  const { isProbationPassed } = storeToRefs(userStore)

  const availableCards = computed(() => {
    const result: { helpCards: Card[]; main: Card[]; right: Card[] } = { helpCards: [], main: [], right: [] }

    CARDS_LIST.left.forEach((card) => {
      if (isModuleEnabled(card.module as SystemModule)) {
        if (card.module === 'faq' || card.module === 'feedback') {
          result.helpCards.push(card)
        } else {
          result.main.push(card)
        }
      }
    })

    CARDS_LIST.right.forEach((card) => {
      if (isModuleEnabled(card.module as SystemModule)) {
        if (['events'].includes(card.module) && !isProbationPassed.value) {
          return
        }
        result.right.push(card)
      }
    })

    return result
  })
</script>

<template>
  <div class="wide-responsive flex w-full flex-col gap-4 p-4 lg:flex-row lg:flex-nowrap!">
    <div class="no-wrap flex w-full flex-col gap-4 lg:w-146.75 lg:min-w-146.75">
      <CoinsBurnNotification />

      <component
        :is="card.component"
        v-for="card in availableCards.main"
        :key="card.module"
        v-bind="card.props"
      />

      <div
        v-if="availableCards.helpCards.length > 0"
        class="flex flex-col gap-6 sm:flex-row"
      >
        <component
          :is="card.component"
          v-for="card in availableCards.helpCards"
          :key="card.module"
          v-bind="card.props"
          class="w-full sm:flex-1"
        />
      </div>
    </div>

    <div
      v-if="availableCards.right.length > 0"
      class="flex w-full flex-col flex-nowrap! gap-4 lg:w-91.25 lg:min-w-91.25"
    >
      <component
        :is="card.component"
        v-for="card in availableCards.right"
        :key="card.module"
        v-bind="card.props"
      />
    </div>
  </div>
</template>
